import { useState } from 'react';
import {
  Button,
  Chip,
  Field,
  FilterBar,
  ListItem,
  Pill,
  Screen,
  ScreenHeader,
  StateBox,
  Workspace,
  WorkspacePane,
  humanize,
  type PillTone,
} from '@garnet/ui';
import type { CaseRecord, VerticalData } from '../data/types.js';

/* Garnet · Case queue + case detail */

type Severity = CaseRecord['severity'];

const SEV: Record<Severity, { tone: PillTone; label: string }> = {
  blocking: { tone: 'f', label: 'blocking' },
  high: { tone: 'a', label: 'high' },
  medium: { tone: 'n', label: 'medium' },
  low: { tone: 'n', label: 'low' },
};

function SevPill({ s }: { s: Severity }) {
  const m = SEV[s];
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

function slaText(h: number): string {
  return h < 0 ? Math.abs(h) + 'h overdue' : h + 'h left';
}

function CaseDetail({ c, onResolve }: { c: CaseRecord; onResolve: (id: string, note: string) => void }) {
  const [rationale, setRationale] = useState(c.resolution ? c.resolution.note : '');
  const [signoff, setSignoff] = useState(false);
  const resolved = c.status === 'resolved';
  const needsSignoff = c.severity === 'blocking';
  const canResolve = rationale.trim().length > 10 && (!needsSignoff || signoff);

  return (
    <div style={{ padding: '22px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{c.id} · {c.recordId} · opened {c.opened}</div>
          <h2 className="display" style={{ fontSize: '1.35rem', fontWeight: 600, margin: '5px 0 4px' }}>{c.supplier}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, background: 'var(--rule-soft)', border: '1px solid var(--rule)', borderRadius: 3, padding: '2px 7px' }}>{humanize(c.flagType)}</span>
            <SevPill s={c.severity} />
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>SLA</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, color: c.slaHrs < 0 ? 'var(--flag)' : 'var(--ink)' }}>{resolved ? 'met' : slaText(c.slaHrs)}</div>
        </div>
      </div>

      <p style={{ fontSize: 14, marginTop: 14, color: 'var(--ink)' }}>{c.summary}</p>

      <div className="evidence" style={{ marginTop: 12 }}>
        <div className="eh"><span>evidence</span><span>{c.trigger}</span></div>
        <pre>{c.evidence}</pre>
      </div>

      {/* resolution */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--rule)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Resolution</div>
        {resolved && c.resolution ? (
          <div className="weak" style={{ borderLeftColor: 'var(--verified)', background: 'var(--verified-bg)' }}>
            <span className="wl" style={{ color: 'var(--verified-ink)' }}>signed<br />off</span>
            <span className="wt"><b>{c.resolution.by}</b> · {c.resolution.at}<br />{c.resolution.note}</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label="Rationale">
              <textarea className="input" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)}
                placeholder="State the decision and the evidence it rests on." style={{ resize: 'vertical', fontFamily: 'var(--body)' }} />
            </Field>
            {needsSignoff && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: 'var(--mono)', fontSize: 12.5 }}>
                <input type="checkbox" checked={signoff} onChange={(e) => setSignoff(e.target.checked)} style={{ width: 16, height: 16 }} />
                Compliance approver sign-off · required to clear a blocking flag
              </label>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" icon="check" disabled={!canResolve} onClick={() => onResolve(c.id, rationale)}>Resolve with sign-off</Button>
              <Button variant="secondary" icon="share">Request counterparty input</Button>
            </div>
            {!canResolve && <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              {rationale.trim().length <= 10 ? 'add a rationale' : 'compliance sign-off required'}
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}

const SEV_FILTERS: Array<'all' | Severity> = ['all', 'blocking', 'high', 'medium', 'low'];

export function Cases({ data }: { data: VerticalData }) {
  const [cases, setCases] = useState<CaseRecord[]>(data.cases);
  const [sel, setSel] = useState<string>(data.cases[0]?.id ?? '');
  const [sev, setSev] = useState<'all' | Severity>('all');

  const list = cases.filter((c) => sev === 'all' || c.severity === sev);
  const c = cases.find((x) => x.id === sel) ?? list[0];

  const resolve = (id: string, note: string) => setCases((cs) => cs.map((x) => x.id === id ? {
    ...x, status: 'resolved', slaHrs: 0,
    resolution: { by: 'S. Lench · compliance approver', at: '2026-06-11 now', note },
  } : x));

  return (
    <Screen workspace>
      <ScreenHeader eyebrow="§ Case queue" title="Flag adjudication" />

      <FilterBar label="severity">
        {SEV_FILTERS.map((x) => (
          <Chip key={x} active={sev === x} onClick={() => setSev(x)}>
            {x}
          </Chip>
        ))}
      </FilterBar>

      <Workspace rail={340}>
        <WorkspacePane>
          <div className="pane-sticky" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 14px' }}>
            Queue · {list.length}
          </div>
          <div className="list">
            {list.map((x) => {
              const active = x.id === c?.id;
              return (
                <ListItem key={x.id} active={active} onSelect={() => setSel(x.id)} style={x.status === 'resolved' ? { opacity: 0.6 } : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{x.id}</span>
                    <SevPill s={x.severity} />
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 5 }}>{x.supplier}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{humanize(x.flagType)}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: x.status === 'resolved' ? 'var(--verified-ink)' : x.slaHrs < 0 ? 'var(--flag)' : 'var(--muted)' }}>
                      {x.status === 'resolved' ? 'resolved' : slaText(x.slaHrs)}
                    </span>
                  </div>
                </ListItem>
              );
            })}
          </div>
        </WorkspacePane>
        {c ? (
          <WorkspacePane>
            <CaseDetail c={c} onResolve={resolve} />
          </WorkspacePane>
        ) : (
          <WorkspacePane flush>
            <StateBox kind="empty" title="No cases in this filter" />
          </WorkspacePane>
        )}
      </Workspace>
    </Screen>
  );
}
